import type { ConfigRowSaveAdapter } from './configRowSaveAdapter'

// The config-modal row vocabulary, extracted to a dependency-free leaf module —
// its one import is `import type`, which TypeScript erases, so a consumer that
// only needs the keys never pulls the modal render tree or an RTK-Query slice
// into its module graph (same reasoning as Settings/tabKeys.ts).

export type RowKey =
  | 'brand_voice'
  | 'recovery_generosity'
  | 'approved_rewards'
  | 'escalation_rules'
  | 'active_locations'
  | 'escalation_contacts'

/** The rows the Agent Tuning tab may open. Unchanged from today's set. */
export const AGENT_TUNING_ROW_KEYS: RowKey[] = [
  'brand_voice',
  'recovery_generosity',
  'approved_rewards',
]

/** The five activation-summary "Change" surfaces, in the summary's card order. */
export const ACTIVATION_ROW_KEYS: RowKey[] = [
  'recovery_generosity',
  'brand_voice',
  'active_locations',
  'approved_rewards',
  'escalation_contacts',
]

/**
 * 'draft'   — stages through RecoveryConfigDraftContext; one PUT recovery/configuration.
 * 'adapter' — owns its staged state and write sequence, because its primary data
 *             is not a RecoveryConfig field: locations ride
 *             PUT recovery/location_configs (the config PUT rejects them) and
 *             recipients ride PUT recovery/escalation_contacts.
 */
export type RowSaveModel = 'draft' | 'adapter'

/**
 * The draft-model rows the SHARED shell renders, i.e. every 'draft' row except
 * brand_voice, which owns its own two-view host and short-circuits the shell.
 *
 *   context: an array rather than a bare union so the registry can narrow with a
 *   type predicate from one source, and so a spec can check it against
 *   ROW_SAVE_MODEL at runtime. Narrowing is what turns "a row with no renderer"
 *   into a compile error: `ROW_SAVE_MODEL[key] === 'draft'` does not narrow `key`,
 *   which is why the registry used to need an unguarded fallthrough.
 */
export const DRAFT_ROW_KEYS = [
  'recovery_generosity',
  'approved_rewards',
  'escalation_rules',
] as const

export type DraftRowKey = typeof DRAFT_ROW_KEYS[number]

export const isDraftRowKey = (key: RowKey): key is DraftRowKey =>
  (DRAFT_ROW_KEYS as readonly RowKey[]).includes(key)

export const ROW_SAVE_MODEL: Record<RowKey, RowSaveModel> = {
  brand_voice: 'draft',
  recovery_generosity: 'draft',
  approved_rewards: 'draft',
  escalation_rules: 'draft',
  active_locations: 'adapter',
  escalation_contacts: 'adapter',
}

/**
 * Per-row dialog width — the DEFAULT for any host that does not override it.
 * `active_locations` ONLY: approved_rewards already ships at ModalContainer's
 * 600px default inside the Agent-Tuning modal, and this map is keyed by ROW, so
 * widening it here would change a shipped modal on every surface at once. Every
 * other row omits a width and inherits that 600px default byte-for-byte.
 */
export const ROW_MODAL_WIDTH: Partial<Record<RowKey, string>> = {
  active_locations: '800px',
}

/**
 * The activation summary's widths, mostly WIDER than the row defaults above.
 *
 *   context: 600px is a Settings-tab width — the panes there sit in a narrow
 *   column. The activation cards' Figma puts these dialogs at roughly 960px
 *   (active locations, whose subset list holds full street addresses on one line)
 *   and 740px (escalation, whose two recipient pickers and rationale copy wrap
 *   badly below that). Rendering them at 600 squeezes the generosity modal's
 *   two-column "More likely to / Less likely to" table to ~200px a side
 *   and pushes every dialog into a tall, thin scroll.
 *
 *   escalation_contacts is 10% wider than that original 740px, and
 *   active_locations is 20% narrower than its original 960px — a follow-up
 *   tuning pass on this same page, independent of the Figma baseline above.
 *
 *   Host-scoped rather than folded into ROW_MODAL_WIDTH on purpose: the same rows
 *   render inside the Agent Tuning tab and the wizard's Testing stage, where the
 *   shipped width is 600px (800px for active_locations) and nothing has asked
 *   for that to change. Whether those surfaces should adopt these numbers too is
 *   a product question, not a consequence of this fix.
 *
 *   brand_voice is absent because it short-circuits the shared shell to render its
 *   own two-view ConfigModalHost, so its width is not this map's to set.
 */
export const ACTIVATION_ROW_MODAL_WIDTH: Partial<Record<RowKey, string>> = {
  active_locations: '768px',
  recovery_generosity: '740px',
  approved_rewards: '740px',
  escalation_contacts: '814px',
}

export type { ConfigRowSaveAdapter }
